import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { visibleTourSteps } from './featureTourSteps';

// ─── Feature tour ─────────────────────────────────────────────────────────────
//
// A reusable guided walkthrough for onboarding people onto a module as it moves
// across from an external app (desk booking replacing Team Today is the first).
// A page defines an ordered list of steps, each pointing at a real element on
// the screen by CSS selector (by convention a [data-tour="..."] attribute), and
// this component spotlights each one in turn with an explanation card.
//
// Deliberately generic: nothing in here knows about desks. To give another
// module a tour, define its steps next to that page and pair this component
// with useFeatureTour (frontend/src/hooks/useFeatureTour.js), which handles
// show-once persistence.
//
// A step is:
//   { title, body, target?, before? }
//   - body: a string or an array of strings, one paragraph each.
//   - target: CSS selector to spotlight. Omitted, or matching nothing (a panel
//     that only renders when there is data), the card simply shows centred —
//     the explanation still reads, it just has nothing to point at.
//   - before: optional function run on entering the step, for opening the
//     <details> or tab the target lives inside. May be async.
//   - when: optional condition, a function of the `context` prop (or a plain
//     boolean). A step that fails it is not shown to this person at all, and
//     is not counted. That is for a control only some roles can see: the
//     centred fallback above is right for a panel that is merely empty
//     today, and wrong for one this person will never have. The filter
//     itself lives in ./featureTourSteps.js.
//
// The spotlight is one absolutely-positioned box whose enormous box-shadow
// dims everything around it — one element, no SVG mask, and the rounded
// corners come free. Its position is re-read every animation frame while the
// tour is open, so it stays glued to the target through the smooth scroll
// that brings the target into view, window resizes, and content shifting
// above it. The overlay swallows clicks, so the page cannot be half-driven
// mid-tour; navigation is the card's buttons or the arrow keys.

const PAD = 8;        // breathing room between the target and the spotlight edge
const GAP = 14;       // between the spotlight and the card
const EDGE = 12;      // minimum distance from the viewport edge

export default function FeatureTour({ steps, context, onFinish, onSkip }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);       // target position, viewport coords
  const [cardPos, setCardPos] = useState(null); // null = centred
  const cardRef = useRef(null);

  // The steps this person is actually shown, which may be fewer than were
  // handed over: see ./featureTourSteps.js. Recomputed every render on
  // purpose, so a caller may pass a fresh context object without memoising
  // it; the surviving step objects are the same references either way, which
  // is what keeps the effects below from re-running.
  const visible = visibleTourSteps(steps, context);
  // Clamped rather than stored, so a condition that flips mid-tour cannot
  // leave the card pointing past the end of a list that just got shorter.
  const idx = Math.min(stepIdx, Math.max(0, visible.length - 1));
  const step = visible[idx];
  const last = idx >= visible.length - 1;

  // Track the target for the current step, frame by frame.
  //
  // Both setters below MUST be value-idempotent (return the previous state
  // when nothing meaningfully moved). The rAF updates sit in React's default
  // lane while the tour's buttons and keys produce sync-lane updates, and
  // React replays pending updaters across those interleaved renders. An
  // updater that mints a fresh object for equal values re-triggers the layout
  // effect, whose own setState schedules the next render, and the two feed
  // each other until React throws "maximum update depth exceeded". This
  // happened, reproducibly, on every keyboard step-change before the guards.
  useEffect(() => {
    let raf = 0, cancelled = false;
    (async () => {
      try { await step?.before?.(); } catch { /* the step still shows */ }
      if (cancelled) return;
      let scrolled = false;
      const tick = () => {
        const el = step?.target ? document.querySelector(step.target) : null;
        // A target that is in the DOM but renders to nothing counts as no
        // target. Wrappers around components that return null when they have
        // no data (a favourites bar for somebody with no favourites yet) are
        // present but 0px, and pointing at one draws an empty spotlight over
        // whatever sits behind it while the card describes something invisible.
        // Falling back to centred is the behaviour a missing element already
        // gets, and every step body is written to read that way.
        const r = el?.getBoundingClientRect();
        if (r && r.width > 0 && r.height > 0) {
          if (!scrolled) {
            scrolled = true;
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
          setRect(prev =>
            prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5
              && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5
              ? prev : { top: r.top, left: r.left, width: r.width, height: r.height });
        } else {
          setRect(null);
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    })();
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [idx, step]);

  // Put the card under the spotlight if it fits, above it if not, and clamped
  // to the viewport either way. Measured from the rendered card so a long step
  // and a short one both land correctly. Keyed on the rect's numbers rather
  // than the rect object so replayed-but-equal updates (see above) do not
  // re-run it.
  useLayoutEffect(() => {
    if (!rect) { setCardPos(null); return; }
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth, ch = card.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight;
    let top;
    if (rect.top + rect.height + GAP + ch <= vh - EDGE) top = rect.top + rect.height + GAP;
    else if (rect.top - GAP - ch >= EDGE) top = rect.top - GAP - ch;
    else top = Math.max(EDGE, Math.min(vh - ch - EDGE, rect.top + rect.height + GAP));
    const left = Math.max(EDGE, Math.min(rect.left, vw - cw - EDGE));
    setCardPos(prev => prev && prev.top === top && prev.left === left ? prev : { top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect?.top, rect?.left, rect?.width, rect?.height, idx]);

  const next = useCallback(() => {
    if (last) onFinish?.();
    else setStepIdx(idx + 1);
  }, [last, onFinish, idx]);
  const back = useCallback(() => setStepIdx(Math.max(idx - 1, 0)), [idx]);

  // Nothing left to show, because every step was conditional and none of the
  // conditions held. Rendering nothing would record no outcome, so the tour
  // would auto-open again on every visit. Count it as done instead.
  useEffect(() => {
    if (!visible.length) onFinish?.();
  }, [visible.length, onFinish]);

  // Keyboard: arrows to move, Escape to skip. Skipping persists, same as the
  // button, so Escape does not become a way to make the tour nag forever.
  useEffect(() => {
    const key = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onSkip?.(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [next, back, onSkip]);

  if (!step) return null;
  const paragraphs = Array.isArray(step.body) ? step.body : [step.body].filter(Boolean);

  // Portalled to <body>, NOT rendered in place: the app scales its content
  // with a density `zoom`, and a fixed-position overlay inside that wrapper
  // has its coordinates scaled again on the way out, while
  // getBoundingClientRect() reports real screen pixels. The mismatch grows
  // with distance from the top-left, drifting every spotlight and pushing
  // cards off screen. Outside the wrapper the two coordinate spaces agree.
  return createPortal(
    <div data-feature-tour className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label={step.title}>
      {rect ? (
        <div
          className="absolute rounded-xl pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.6)',
            border: '2px solid rgba(255, 255, 255, 0.85)',
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: 'rgba(15, 23, 42, 0.6)' }} />
      )}

      <div
        ref={cardRef}
        className="absolute bg-white rounded-xl border border-gray-200 shadow-2xl w-[22rem] max-w-[calc(100vw-24px)] p-4"
        style={cardPos || { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[14px] font-bold text-gray-900">{step.title}</h3>
          <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">
            {idx + 1} of {visible.length}
          </span>
        </div>
        <div className="mt-2 space-y-2">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-[12px] leading-relaxed text-gray-600">{p}</p>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2">
          {!last && (
            <button type="button" onClick={onSkip}
              className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
              Skip tour
            </button>
          )}
          <div className="ml-auto flex gap-1.5">
            {idx > 0 && (
              <button type="button" onClick={back}
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-[12px] font-semibold hover:bg-gray-50">
                Back
              </button>
            )}
            <button type="button" onClick={next}
              className="px-3.5 py-1.5 rounded-lg bg-navy text-white text-[12px] font-semibold hover:bg-navy/90">
              {last ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
